import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import config from "../../config";
import { formatApiError } from "../../utilities/apiErrorMessages";
import { coalesceAsync, invalidateCoalescedAsync } from "../../utilities/coalesceAsync";
const CLIENTS_LIST_CACHE_KEY = "get-clients:";

const normalizeClient = (client) => ({
  ...client,
  dentists: Array.isArray(client?.dentists) ? client.dentists : [],
  deliveryCenters: Array.isArray(client?.deliveryCenters) ? client.deliveryCenters : Array.isArray(client?.delivery_centers) ? client.delivery_centers : [],
});

const extractClientsPayload = (payload) => {
  const basePagination = {
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
  };

  if (Array.isArray(payload)) {
    const items = payload;
    return {
      items,
      pagination: {
        ...basePagination,
        per_page: items.length || basePagination.per_page,
        total: items.length,
      },
    };
  }

  if (Array.isArray(payload?.data?.data)) {
    const paginator = payload.data ?? {};
    const items = Array.isArray(paginator.data) ? paginator.data : [];
    return {
      items,
      pagination: {
        current_page: Number(paginator.current_page ?? basePagination.current_page),
        per_page: Number(
          paginator.per_page ?? paginator.perPage ?? (items.length || basePagination.per_page)
        ),
        total: Number(paginator.total ?? items.length ?? basePagination.total),
        last_page: Number(paginator.last_page ?? paginator.last ?? basePagination.last_page),
      },
    };
  }

  if (Array.isArray(payload?.data)) {
    const items = payload.data;
    return {
      items,
      pagination: {
        ...basePagination,
        per_page: items.length || basePagination.per_page,
        total: items.length,
      },
    };
  }

  return { items: [], pagination: basePagination };
};

export const GetClients = createAsyncThunk(
  "clients/getAll",
  async (
    {
      token,
      page = 1,
      perPage,
      filters = {},
      trackPagination = false,
      fetchAll = false,
      minimal = false,
    } = {},
    thunkAPI
  ) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;

    try {
      const searchParams = new URLSearchParams();

      if (trackPagination) {
        const effectivePerPage = perPage ?? 10;
        searchParams.set("page", String(page ?? 1));
        searchParams.set("per_page", String(effectivePerPage));
      } else if (fetchAll && perPage !== undefined && perPage !== null) {
        searchParams.set("per_page", String(perPage));
      }

      const mergedFilters = { ...filters };
      if (minimal || fetchAll) {
        // Catalog / select UIs only need id+name — skip nested dentists/DCs.
        mergedFilters.minimal = 1;
      }

      Object.entries(mergedFilters).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") {
          return;
        }
        if (Array.isArray(value)) {
          if (value.length === 0) return;
          searchParams.append(key, value.join(','));
          return;
        }

        searchParams.append(key, value);
      });

      const query = searchParams.toString();
      const url = `${config.apiUrl}/clients${query ? `?${query}` : ""}`;
      const requestKey = `get-clients:${url}`;

      const data = await coalesceAsync(
        requestKey,
        async () => {
          const response = await fetch(url, {
            method: "GET",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          });

          const body = await response.json();

          if (!response.ok) {
            const error = new Error(body?.message ?? "Unable to fetch clients");
            error.payload = body;
            error.status = response.status;
            throw error;
          }

          return body;
        },
        { ttlMs: 1500 }
      );

      return fulfillWithValue(data);
    } catch (error) {
      if (error?.payload) {
        return rejectWithValue(error.payload);
      }
      return rejectWithValue({
        message: error?.message ?? "Unable to fetch clients",
      });
    }
  }
);

export const AddClient = createAsyncThunk(
  "clients/create",
  async ({ token, values }, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;

    try {
      const response = await fetch(`${config.apiUrl}/clients`, {
        method: "POST",
        body: JSON.stringify(values),
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      invalidateCoalescedAsync(CLIENTS_LIST_CACHE_KEY);
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue({
        message: error?.message ?? "Unable to create client",
      });
    }
  }
);

export const UpdateClient = createAsyncThunk(
  "clients/update",
  async ({ token, id, values }, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;

    try {
      const response = await fetch(`${config.apiUrl}/clients/${id}`, {
        method: "PUT",
        body: JSON.stringify(values),
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      invalidateCoalescedAsync(CLIENTS_LIST_CACHE_KEY);
      invalidateCoalescedAsync(`client-show:${id}`);
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue({
        message: error?.message ?? "Unable to update client",
      });
    }
  }
);

export const DeleteClient = createAsyncThunk(
  "clients/delete",
  async ({ token, id }, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;

    try {
      const response = await fetch(`${config.apiUrl}/clients/${id}`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = response.status === 204 ? {} : await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return fulfillWithValue({
        id,
        message: data?.message ?? null,
      });
    } catch (error) {
      return rejectWithValue({
        message: error?.message ?? "Unable to delete client",
      });
    }
  }
);

const initialState = {
  clients: [],
  paginatedClients: [],
  pagination: {
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
  },
  loading: false,
  listRequestId: null,
  allClientsLoaded: false,
  error: null,
  successMessage: null,
  selectedClient: null,
};

const isStaleListResponse = (state, action) =>
  Boolean(action.meta?.arg?.trackPagination) &&
  Boolean(state.listRequestId) &&
  action.meta.requestId !== state.listRequestId;

const ClientsSlice = createSlice({
  name: "clients",
  initialState,
  reducers: {
    selectClient: (state, action) => {
      state.selectedClient = action.payload;
    },
    clearClientFeedback: (state) => {
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(GetClients.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
        if (action.meta?.arg?.trackPagination) {
          state.listRequestId = action.meta.requestId;
        }
      })
      .addCase(GetClients.fulfilled, (state, action) => {
        if (isStaleListResponse(state, action)) {
          return;
        }
        state.loading = false;
        const { items, pagination } = extractClientsPayload(action.payload);
        const normalized = items.map(normalizeClient);
        if (action.meta?.arg?.trackPagination) {
          state.paginatedClients = normalized;
          state.pagination = pagination;
        } else {
          state.clients = normalized;
          state.allClientsLoaded = true;
        }
        state.error = null;
        state.successMessage = null;
      })
      .addCase(GetClients.rejected, (state, action) => {
        if (isStaleListResponse(state, action)) {
          return;
        }
        state.loading = false;
        if (action.meta?.arg?.trackPagination) {
          state.paginatedClients = [];
          state.pagination = {
            ...state.pagination,
            total: 0,
          };
        } else {
          state.clients = [];
          state.allClientsLoaded = true;
        }
        state.error = formatApiError(action.payload, "Unable to fetch clients");
        state.successMessage = null;
      })
      .addCase(AddClient.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(AddClient.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload?.message ?? null;
        state.error = null;
        const created = action.payload?.data;
        if (created) {
          state.clients = [...state.clients, normalizeClient(created)];
        }
      })
      .addCase(AddClient.rejected, (state, action) => {
        state.loading = false;
        state.error = formatApiError(action.payload, "Unable to create client");
        state.successMessage = null;
      })
      .addCase(UpdateClient.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(UpdateClient.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload?.message ?? null;
        state.error = null;
        const updated = action.payload?.data ? normalizeClient(action.payload.data) : null;
        if (updated) {
          const index = state.clients.findIndex((client) => client.id === updated.id);
          if (index !== -1) {
            state.clients[index] = {
              ...state.clients[index],
              ...updated,
            };
          }
        }
      })
      .addCase(UpdateClient.rejected, (state, action) => {
        state.loading = false;
        state.error = formatApiError(action.payload, "Unable to update client");
        state.successMessage = null;
      })
      .addCase(DeleteClient.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(DeleteClient.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload?.message ?? null;
        state.error = null;
        const id = action.payload?.id;
        if (id != null) {
          state.clients = state.clients.filter((client) => client.id !== id);
          state.paginatedClients = state.paginatedClients.filter((client) => client.id !== id);
          state.pagination = {
            ...state.pagination,
            total: Math.max(0, (state.pagination?.total ?? 0) - 1),
          };
        }
      })
      .addCase(DeleteClient.rejected, (state, action) => {
        state.loading = false;
        state.error = formatApiError(action.payload, "Unable to delete client");
        state.successMessage = null;
      });
  },
});

export default ClientsSlice.reducer;
export const { selectClient, clearClientFeedback } = ClientsSlice.actions;
